// Keep the exact original upload identity in this tab across signup/reload.
// Never persist an ID token, signed URL, document text, or converted object key.
// Keep only this upload's scoped proof and private signup claim for recovery.
const STORAGE_KEY = "jungle:anonymous-upload-claims:v1";
const MAX_AGE_MS = 24 * 60 * 60 * 1_000;
const MAX_PENDING_CLAIMS = 20;

interface AnonymousUploadClaim {
  taskId: string;
  createdAt: number;
  processingUrl: string;
  body: {
    file_name: string;
    uploaded_file_s3_object_key: string;
    content_medium_type: string;
    upload_claim_token?: string;
    upload_proof?: Record<string, string>;
  };
}

// Some browsers allow reads while refusing writes (quota/private mode). Keep
// the same bounded capability list for this page lifetime in that case.
let memoryClaims: AnonymousUploadClaim[] | null = null;

function readClaims(): AnonymousUploadClaim[] {
  try {
    const stored: unknown = memoryClaims ?? JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "[]");
    if (!Array.isArray(stored)) return [];
    const now = Date.now();
    return stored.filter((claim): claim is AnonymousUploadClaim => {
      if (!claim || typeof claim !== "object") return false;
      // Validate the original source identity and the bounded retention window.
      const hasValidIdentity = typeof claim.taskId === "string" && typeof claim.processingUrl === "string";
      const isWithinRetentionWindow = Number.isFinite(claim.createdAt) && claim.createdAt <= now &&
        now - claim.createdAt < MAX_AGE_MS;
      const hasValidSourceTuple = typeof claim.body?.file_name === "string" &&
        typeof claim.body?.uploaded_file_s3_object_key === "string" &&
        typeof claim.body?.content_medium_type === "string";
      return hasValidIdentity && isWithinRetentionWindow && hasValidSourceTuple;
    }).slice(-MAX_PENDING_CLAIMS);
  } catch {
    return [];
  }
}

function writeClaims(claims: AnonymousUploadClaim[]): void {
  try {
    if (claims.length) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(claims));
    else sessionStorage.removeItem(STORAGE_KEY);
    memoryClaims = null;
  } catch {
    memoryClaims = claims.slice(-MAX_PENDING_CLAIMS);
  }
}

export function rememberAnonymousUpload(taskId: string, processingUrl: string, body: Record<string, unknown>): void {
  if (typeof body.file_name !== "string" || typeof body.uploaded_file_s3_object_key !== "string" ||
      typeof body.content_medium_type !== "string") return;
  const claims = readClaims().filter((claim) => claim.taskId !== taskId);
  claims.push({
    taskId, processingUrl, createdAt: Date.now(),
    body: {
      file_name: body.file_name,
      uploaded_file_s3_object_key: body.uploaded_file_s3_object_key,
      content_medium_type: body.content_medium_type,
      ...(typeof body.upload_claim_token === "string" ? { upload_claim_token: body.upload_claim_token } : {}),
      ...(body.upload_proof && typeof body.upload_proof === "object"
        ? { upload_proof: body.upload_proof as Record<string, string> } : {}),
    },
  });
  writeClaims(claims.slice(-MAX_PENDING_CLAIMS));
}

export function getAnonymousUploadClaim(taskId: string, processingUrl: string): AnonymousUploadClaim | undefined {
  const claims = readClaims();
  writeClaims(claims);
  return claims.find((claim) => claim.taskId === taskId && claim.processingUrl === processingUrl);
}

export function forgetAnonymousUpload(taskId: string): void {
  writeClaims(readClaims().filter((claim) => claim.taskId !== taskId));
}

/** Keep the source capability out of deck metadata and source diagnostics. */
export function retainServerSourceReceipt<T>(result: T, processingUrl: string): T {
  // Leave malformed legacy responses to the caller's existing validation.
  if (!result || typeof result !== "object" || Array.isArray(result)) return result;
  const record = result as Record<string, unknown>;
  const { upload_claim_token, ...safeResult } = record;
  if (typeof record.study_guide_source_receipt_id === "string" &&
      typeof record.uploaded_file_s3_object_key === "string" &&
      typeof upload_claim_token === "string" && /^[a-f0-9]{64}$/.test(upload_claim_token)) {
    rememberAnonymousUpload(record.study_guide_source_receipt_id, processingUrl, {
      file_name: record.uploaded_file_s3_object_key,
      uploaded_file_s3_object_key: record.uploaded_file_s3_object_key,
      content_medium_type: "PDF", upload_claim_token,
    });
  }
  return safeResult as T;
}
